import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, Empty, Restricted } from "../components/Bits";
import { Button } from "../components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Analytics() {
  const { t, lang } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const showFin = can("finance.view");
  const showEff = can("efficiency.view");
  useEffect(() => { api.get(`/analytics/monthly?month=${month}`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [month]);

  const sum = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0);
  const exportXlsx = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/analytics/export?month=${month}&lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `analytics_${month}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error(t("error")); } finally { setBusy(false); }
  };
  const effColor = (e) => e >= 100 ? "var(--green)" : e >= 85 ? "var(--amber)" : "var(--red)";

  return (
    <div data-testid="analytics-page">
      <PageHeader title={t("analytics")} subtitle={t("monthly_table")}
        action={<div className="flex gap-2 items-center">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border rounded-md h-10 px-2 text-sm bg-[var(--card)]" data-testid="analytics-month" />
          {can("analytics.export") && <Button onClick={exportXlsx} disabled={busy} className="bg-[var(--green)] text-white gap-2" data-testid="export-excel"><FileSpreadsheet size={16} /> {t("export_excel")}</Button>}
        </div>} />
      {!showFin && <div className="mb-3"><Restricted /></div>}
      {rows.length === 0 ? <Empty /> : (<>
        <div className="hidden md:block tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-[var(--text-3)]">
              <th className="p-3">{t("name")}</th>
              <th className="p-3 text-right">{t("days")}</th>
              <th className="p-3 text-right">{t("actual_hours")}</th>
              <th className="p-3 text-right">{t("productive_time")}</th>
              <th className="p-3 text-right">{t("non_productive_time")}</th>
              <th className="p-3 text-right">{t("unallocated")}</th>
              {showEff && <th className="p-3 text-right">{t("norm_hours")}</th>}
              {showEff && <th className="p-3 text-right">{t("efficiency")}</th>}
              <th className="p-3 text-right">{t("reports")}</th>
              {showFin && <th className="p-3 text-right">{t("cost")}</th>}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`analytics-${r.id}`} className="border-b hover:bg-[var(--bg)]">
                  <td className="p-3 font-semibold">{r.name}</td>
                  <td className="p-3 text-right font-mono-num">{r.days}</td>
                  <td className="p-3 text-right font-mono-num">{r.actual_hours}h</td>
                  <td className="p-3 text-right font-mono-num" data-testid={`prod-${r.id}`}>{r.productive_minutes}′</td>
                  <td className="p-3 text-right font-mono-num" data-testid={`nonprod-${r.id}`}>{r.non_productive_minutes}′</td>
                  <td className="p-3 text-right font-mono-num" data-testid={`unalloc-${r.id}`} style={{ color: r.unallocated_minutes < 0 ? "var(--red)" : r.unallocated_minutes > 0 ? "var(--amber)" : "inherit" }}>{r.unallocated_minutes}′</td>
                  {showEff && <td className="p-3 text-right font-mono-num">{r.norm_hours}h</td>}
                  {showEff && <td className="p-3 text-right font-mono-num font-bold" style={{ color: effColor(r.efficiency) }}>{r.efficiency}%</td>}
                  <td className="p-3 text-right font-mono-num">{r.reports}</td>
                  {showFin && <td className="p-3 text-right font-mono-num">€{r.cost ?? 0}</td>}
                </tr>
              ))}
              <tr className="font-bold bg-[var(--bg)]">
                <td className="p-3">{t("total")}</td>
                <td className="p-3 text-right font-mono-num">{sum("days")}</td>
                <td className="p-3 text-right font-mono-num">{sum("actual_hours").toFixed(1)}h</td>
                <td className="p-3 text-right font-mono-num">{sum("productive_minutes")}′</td>
                <td className="p-3 text-right font-mono-num">{sum("non_productive_minutes")}′</td>
                <td className="p-3 text-right font-mono-num">{sum("unallocated_minutes")}′</td>
                {showEff && <td className="p-3 text-right font-mono-num">{sum("norm_hours").toFixed(1)}h</td>}
                {showEff && <td className="p-3 text-right font-mono-num" data-testid="analytics-total-eff">{sum("productive_minutes") ? Math.round(sum("norm_minutes") / sum("productive_minutes") * 100) : 0}%</td>}
                <td className="p-3 text-right font-mono-num">{sum("reports")}</td>
                {showFin && <td className="p-3 text-right font-mono-num">€{sum("cost").toFixed(2)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="tactical-card p-3 text-sm" data-testid={`analytics-card-${r.id}`}>
              <div className="flex justify-between items-center"><span className="font-semibold">{r.name}</span>{showEff && <span className="font-mono-num font-bold" style={{ color: effColor(r.efficiency) }}>{r.efficiency}%</span>}</div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-[var(--text-3)]">
                <div>{t("days")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.days}</div></div>
                <div>{t("actual_hours")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.actual_hours}h</div></div>
                <div>{t("productive_time")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.productive_minutes}′</div></div>
                <div>{t("non_productive_time")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.non_productive_minutes}′</div></div>
                <div>{t("unallocated")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.unallocated_minutes}′</div></div>
                {showEff && <div>{t("norm_hours")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.norm_hours}h</div></div>}
                <div>{t("reports")}<div className="font-mono-num text-[var(--text)] font-semibold">{r.reports}</div></div>
                {showFin && <div>{t("cost")}<div className="font-mono-num text-[var(--text)] font-semibold">€{r.cost}</div></div>}
              </div>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}
